import { useQueryClient, InfiniteData } from "@tanstack/react-query";
import useChatSocket from "hooks/useChatSocket";
import { ConversationListResponseDto, ConversationDto } from "libs/cellera-sdk";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ChatSubscribeMessage,
  ChatMessage,
  SenderType,
  NewConversationMessageReponse,
} from "types/socket";

type NotificationContextValue = {
  hasUnreadMessages: boolean;
  unreadCount: number;
  clearUnreadMessages: () => void;
  setSelectedConversationId: (conversationId: string | undefined) => void;
  simulatePatientMessage?: (messageId: string) => void; // For testing only
};

export const NotificationContext = createContext<NotificationContextValue>({
  hasUnreadMessages: false,
  unreadCount: 0,
  clearUnreadMessages: () => {},
  setSelectedConversationId: () => {},
  simulatePatientMessage: () => {},
});

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { socket } = useChatSocket();
  const queryClient = useQueryClient();
  const [selectedConversationId, setSelectedConversationIdState] = useState<
    string | undefined
  >();

  // Expose setter for selected conversation ID
  const setSelectedConversationId = useCallback(
    (conversationId: string | undefined) => {
      setSelectedConversationIdState(conversationId);
    },
    []
  );

  // Track unread count from conversations API
  const [unreadCountFromConversations, setUnreadCountFromConversations] =
    useState(0);

  // Calculate unread count from conversations data
  useEffect(() => {
    const calculateUnreadCount = () => {
      const conversationsData = queryClient.getQueryData<
        InfiniteData<ConversationListResponseDto>
      >(["conversations"]);
      if (!conversationsData?.pages) {
        setUnreadCountFromConversations(0);
        return;
      }
      const conversations = conversationsData.pages.flatMap(
        (page: ConversationListResponseDto) => page.conversations || []
      );
      const total = conversations.reduce(
        (sum: number, conv: ConversationDto) => {
          return sum + (conv.unreadCountProvider || 0);
        },
        0
      );
      setUnreadCountFromConversations(total);
    };

    // Calculate initially
    calculateUnreadCount();

    // Subscribe to query cache updates
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event?.query?.queryKey?.[0] === "conversations") {
        calculateUnreadCount();
      }
    });

    return unsubscribe;
  }, [queryClient]);

  // Play notification sound - plays for every new message
  const playNotificationSound = useCallback(() => {
    try {
      // Create an audio context and play a simple notification sound
      const AudioContextClass =
        window.AudioContext ||
        ((window as typeof window & { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext as typeof AudioContext);
      const audioContext = new AudioContextClass();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Configure sound: frequency, type, and volume
      oscillator.frequency.value = 800; // Higher pitch for notification
      oscillator.type = "sine";
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(
        0.01,
        audioContext.currentTime + 0.3
      );

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.warn("Failed to play notification sound:", error);
    }
  }, []);

  // Listen for new messages from patients
  useEffect(() => {
    if (!socket) return;

    const onNewMessage = (message: ChatMessage) => {
      // Only track messages from patients
      if (message.senderType === SenderType.PATIENT) {
        // Check if we're viewing this specific conversation
        // If we are, don't show notification (message will be auto-read)
        const isViewingThisConversation =
          selectedConversationId === message.conversationId;

        if (!isViewingThisConversation) {
          // Play sound notification when receiving new patient message
          // Only if not viewing that conversation
          playNotificationSound();

          // Optimistically update query cache to show badge immediately
          // Update unreadCountProvider for the conversation that received the message
          queryClient.setQueryData<InfiniteData<ConversationListResponseDto>>(
            ["conversations"],
            (oldData) => {
              if (!oldData) return oldData;

              return {
                ...oldData,
                pages: oldData.pages.map((page) => ({
                  ...page,
                  conversations: page.conversations?.map((conv) =>
                    conv.id === message.conversationId
                      ? {
                          ...conv,
                          unreadCountProvider:
                            (conv.unreadCountProvider || 0) + 1,
                        }
                      : conv
                  ),
                })),
              };
            }
          );
        }
      }
    };

    const onNewConversationMessage = (
      message: NewConversationMessageReponse
    ) => {
      // Handle NEW_CONVERSATION_MESSAGE event
      if (message.senderType === SenderType.PATIENT) {
        // Check if we're viewing this specific conversation
        const isViewingThisConversation =
          selectedConversationId === message.conversationId;

        if (!isViewingThisConversation) {
          // Play sound notification for new conversation messages from patients
          // Only if not viewing that conversation
          playNotificationSound();

          // Optimistically update query cache to show badge immediately
          // Update unreadCountProvider for the conversation that received the message
          queryClient.setQueryData<InfiniteData<ConversationListResponseDto>>(
            ["conversations"],
            (oldData) => {
              if (!oldData) return oldData;

              return {
                ...oldData,
                pages: oldData.pages.map((page) => ({
                  ...page,
                  conversations: page.conversations?.map((conv) =>
                    conv.id === message.conversationId
                      ? {
                          ...conv,
                          unreadCountProvider:
                            (conv.unreadCountProvider || 0) + 1,
                        }
                      : conv
                  ),
                })),
              };
            }
          );
        }
      }
    };

    socket.on(ChatSubscribeMessage.NEW_MESSAGE, onNewMessage);
    socket.on(
      ChatSubscribeMessage.NEW_CONVERSATION_MESSAGE,
      onNewConversationMessage
    );

    return () => {
      socket.off(ChatSubscribeMessage.NEW_MESSAGE, onNewMessage);
      socket.off(
        ChatSubscribeMessage.NEW_CONVERSATION_MESSAGE,
        onNewConversationMessage
      );
    };
  }, [socket, selectedConversationId, playNotificationSound, queryClient]);

  // Clear unread messages function (for manual clearing if needed)
  const clearUnreadMessages = useCallback(() => {
    // No-op: unread count is managed by API
  }, []);

  // Simulate patient message for testing (triggers sound only)
  const simulatePatientMessage = useCallback(() => {
    playNotificationSound();
  }, [playNotificationSound]);

  // Badge should be based on API unread count (unreadCountProvider from conversations)
  // Socket messages optimistically update the cache, so badge appears immediately
  const hasUnreadMessages = useMemo(() => {
    return unreadCountFromConversations > 0;
  }, [unreadCountFromConversations]);

  const value = useMemo(
    () => ({
      hasUnreadMessages,
      unreadCount: unreadCountFromConversations,
      clearUnreadMessages,
      setSelectedConversationId,
      simulatePatientMessage,
    }),
    [
      hasUnreadMessages,
      unreadCountFromConversations,
      clearUnreadMessages,
      setSelectedConversationId,
      simulatePatientMessage,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  return useContext(NotificationContext);
}
